import { PromotionPlanInfo, PromotionPlanType, PromotionPlanVariant } from 'store/sagas/apiService/types';
import { EnhancedPromotionPlanInfo, Feature } from './modals/private-bulletin-select-promotion-plan-modal/types';
import { ITranslate } from 'locale';
import { minutesToHours } from 'helpers/timeUtils';

// Generates the URL the server uses to redirect back to the client after payment, triggering the payment result modal.
export function getRedirectPaymentResultUrl(key: string) {
  return `${window.location.origin}/manage-bulletins?payment_modal=true&uuk=${key}`;
}

function getPromotionPlanTitle(plan: PromotionPlanInfo, t: ITranslate): string {
  if (plan.isUserEligibleForGrant) {
    return t('promotionPlanInfoCard.planTitle', { key: 'GRANT' });
  }
  return t('promotionPlanInfoCard.planTitle', { key: plan.variant });
}

export const planEnhancer = (plans: PromotionPlanInfo[], t: ITranslate): EnhancedPromotionPlanInfo[] => {

  const additionalDataByVariant: { [key in PromotionPlanVariant]: (p: PromotionPlanInfo) => EnhancedPromotionPlanInfo } = {
    [PromotionPlanVariant.BASIC]: (p): EnhancedPromotionPlanInfo => {
      const hours = minutesToHours(p.intervalMinutes);
      return {
        ...p,
        features: [
          {
            id: Feature.ManualPromotion,
            text: t('privateBulletin.promotion.mobile.modal.manualPromotion', { hours }),
          },
          {
            id: Feature.TargetedBuyers,
            text: t('privateBulletin.promotion.mobile.modal.targetedBuyersFeature', { isActive: false }),
            disabled: true,
          },
          {
            id: Feature.WhatsApp,
            text: t('privateBulletin.promotion.mobile.modal.whatsAppFeature', { isActive: false }),
            disabled: true,
          },
        ],
        durationDays: null,
        intervalMinutes: null,
        price: null,
        cardVariant: 'secondary',
        cardCornerLabel: {
          text: t('privateBulletin.promotion.modal.durationDays.free', { isMobile: true }),
        },
        planTitle: getPromotionPlanTitle(p, t),
      };
    },
    [PromotionPlanVariant.MINI]: (p): EnhancedPromotionPlanInfo => {
      const price = p.isUserEligibleForGrant ? 0 : p.price;
      const priceSubtext = '/ לשבועיים';
      const cardCornerLabelText = p.isUserEligibleForGrant ? t('privateBulletin.promotion.modal.cornerLabel.grant') : t('privateBulletin.promotion.modal.durationDays', { durationDays: p.durationDays });
      const showCountdown = Boolean(!p.isUserEligibleForGrant && p.offerValidUntil);
      const cardDescription = p.isUserEligibleForGrant
        ? `במסגרת הסדר פשרה בתובענה ייצוגית ת"צ\n 66117-12-23 קיבלת הטבה אישית: קידום מודעות חינם למשך 14 יום (בשווי 129₪).\n הטבה זו זמינה עד ליום 9.2.2028`
        : null;
      const hours = minutesToHours(p.intervalMinutes);
      return {
        ...p,
        features: [
          {
            id: Feature.AutoPromotion,
            text: t('privateBulletin.promotion.mobile.modal.autoPromotionFeature', { isActive: true, hours }),
          },
          {
            id: 'enhancedVisibility',
            text: t('privateBulletin.promotion.modal.mini.enhancedVisibilityFeature'),
          },
        ],
        cardDescription,
        price,
        priceSubtext,
        cardVariant: 'secondary',
        cardCornerLabel: {
          text: cardCornerLabelText,
          showCountdown,
        },
        planTitle: getPromotionPlanTitle(p, t),
        submitButtonText: t('privateBulletin.promotion.modal.mini.submitButton'),
        planSubtitle: t('privateBulletin.promotion.modal.mini.planSubtitle'),
      };
    },
    [PromotionPlanVariant.ADVANCE]: (p): EnhancedPromotionPlanInfo => {
      const hours = minutesToHours(p.intervalMinutes);
      return {
        ...p,
        features: [
          {
            id: Feature.AutoPromotion,
            text: t('privateBulletin.promotion.mobile.modal.autoPromotionFeature', { isActive: true, hours }),
          },
          {
            id: Feature.TargetedBuyers,
            text: t('privateBulletin.promotion.mobile.modal.targetedBuyersFeature', { isActive: true }),
          },
          {
            id: Feature.WhatsApp,
            text: t('privateBulletin.promotion.mobile.modal.whatsAppFeature', { isActive: true }),
          },
        ],
        fullPrice: 299,
        cardVariant: 'primary',
        cardCornerLabel: {
          text: t('privateBulletin.promotion.modal.durationDays', { durationDays: p.durationDays }),
        },
        planTitle: t('promotionPlanInfoCard.planTitle', { key: 'ADVANCE' }),
      };
    },
    [PromotionPlanVariant.PREMIUM]: (p): EnhancedPromotionPlanInfo => {
      const hours = minutesToHours(p.intervalMinutes);
      return {
        ...p,
        features: [
          {
            id: Feature.AutoPromotion,
            text: t('privateBulletin.promotion.mobile.modal.autoPromotionFeature', { isActive: true, hours }),
          },
          {
            id: Feature.TargetedBuyers,
            text: t('privateBulletin.promotion.mobile.modal.targetedBuyersFeature', { isActive: true }),
          },
          {
            id: Feature.WhatsApp,
            text: t('privateBulletin.promotion.mobile.modal.whatsAppFeature', { isActive: true }),
          },
        ],
        fullPrice: 399,
        cardVariant: 'secondary',
        cardCornerLabel: {
          text: t('privateBulletin.promotion.modal.durationDays', { durationDays: p.durationDays }),
        },
        planTitle: getPromotionPlanTitle(p, t),
      };
    },
    [PromotionPlanVariant.UNTIL_SOLD]: (p): EnhancedPromotionPlanInfo => {
      return {
        ...p,
        features: [
          {
            id: 'prominentTopResults',
            text: t('privateBulletin.promotion.modal.untilSold.prominentTopResultsFeature'),
          },
          {
            id: 'oneTimePayment',
            text: t('privateBulletin.promotion.modal.untilSold.oneTimePaymentFeature'),
          },
          {
            id: Feature.TargetedBuyers,
            text: t('privateBulletin.promotion.mobile.modal.targetedBuyersFeature', { isActive: true }),
          },
          {
            id: Feature.WhatsApp,
            text: t('privateBulletin.promotion.mobile.modal.whatsAppFeature', { isActive: true }),
          },
        ],
        priceSubtext: '/ חד פעמי',
        cardVariant: 'primary',
        cardCornerLabel: {
          text: t('privateBulletin.promotion.modal.oneTimePayment'),
        },
        planTitle: getPromotionPlanTitle(p, t),
        planSubtitle: t('promotionPlanInfoCard.planSubtitle', { key: p.variant }),
        submitButtonText: t('privateBulletin.promotion.modal.untilSold.submitButton'),
      };
    },
    [PromotionPlanVariant.SUBSCRIPTION_MONTHLY]: (p): EnhancedPromotionPlanInfo => {
      const hours = minutesToHours(p.intervalMinutes);
      return {
        ...p,
        features: [
          {
            id: Feature.AutoPromotion,
            text: t('privateBulletin.promotion.mobile.modal.autoPromotionFeature', { isActive: true, hours }),
          },
          {
            id: Feature.TargetedBuyers,
            text: t('privateBulletin.promotion.mobile.modal.targetedBuyersFeature', { isActive: true }),
          },
          {
            id: Feature.WhatsApp,
            text: t('privateBulletin.promotion.mobile.modal.whatsAppFeature', { isActive: true }),
          },
        ],
        cardVariant: 'secondary',
        priceSubtext: '/ לחודש, מתחדש אוטומטית',
        cardCornerLabel: {
          text: t('privateBulletin.promotion.modal.flexiblePayment'),
        },
        planTitle: getPromotionPlanTitle(p, t),
        planSubtitle: t('promotionPlanInfoCard.planSubtitle', { key: p.variant }),
        submitButtonText: t('privateBulletin.promotion.modal.subscriptionMonthly.submitButton'),
      };
    },
  };

  return plans.map(plan => additionalDataByVariant[plan.variant](plan));
};

export function getPromotionPlanPriceText(t: ITranslate, plan: PromotionPlanInfo) {
  const { price, isUserEligibleForGrant } = plan;

  if (isUserEligibleForGrant) return `${price} ₪`;

  return price ? `${price} ₪` : t('privateBulletin.promotion.mobile.modal.freeText');
}

export function getPromotionPlanFullPriceText(price: number) {
  return `${price} ₪`;
}

export function shouldBypassHostedFields(plan: PromotionPlanInfo, isHostedFieldsMode: boolean): boolean {
  return (plan.isUserEligibleForGrant === true && plan.type === PromotionPlanType.MINI) || !isHostedFieldsMode;
}

export function sortPlansForDisplay<T extends { variant: PromotionPlanVariant }>(plans: T[]): T[] {
  const orderedVariants = [
    PromotionPlanVariant.MINI,
    PromotionPlanVariant.ADVANCE,
    PromotionPlanVariant.PREMIUM,
    PromotionPlanVariant.SUBSCRIPTION_MONTHLY,
    PromotionPlanVariant.UNTIL_SOLD,
  ];
  return plans.sort((a, b) => orderedVariants.indexOf(a.variant) - orderedVariants.indexOf(b.variant));
}

export function getPlansForDisplay<T extends { variant: PromotionPlanVariant }>(plans: T[], allowedToShow: PromotionPlanVariant[]): T[] {
  const filtered = plans.filter(item => allowedToShow.includes(item.variant));
  return sortPlansForDisplay(filtered);
}
